// pages/Contact.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  Loader,
  Globe,
  Headphones,
  FileText,
  HelpCircle,
  ExternalLink,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  ChevronRight,
  Star,
  Shield,
  Zap
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  const services = [
    'Web Development',
    'WordPress Development',
    'Frontend Development',
    'Backend Development',
    'E-commerce',
    'Digital Marketing',
    'Video Editing',
    'Graphic Designing',
    'Full Stack Development',
    'Other'
  ];

  const budgets = [
    'Less than $5,000',
    '$5,000 - $15,000',
    '$15,000 - $30,000',
    '$30,000 - $50,000',
    '$50,000+',
    'Not sure yet'
  ];

  const contactMethods = [
    {
      icon: <Phone />,
      title: 'Call Us',
      details: ['+92 304 330 9005', '+92 300 016 9005'],
      description: 'Available Monday-Friday, 9AM-6PM EST',
      action: 'Call Now',
      color: 'blue'
    },
    {
      icon: <Mail />,
      title: 'Email Us',
      details: ['hello@razatechsolutions.com', 'support@razatechsolutions.com'],
      description: 'Typically respond within 2 business hours',
      action: 'Send Email',
      color: 'green'
    },
    {
      icon: <MessageSquare />,
      title: 'Live Chat',
      details: ['Available 24/7'],
      description: 'Instant support via our website chat',
      action: 'Start Chat',
      color: 'purple'
    },
    {
      icon: <Calendar />,
      title: 'Schedule Meeting',
      details: ['30-min consultation'],
      description: 'Book a time that works for you',
      action: 'Book Now',
      color: 'orange'
    }
  ];

  const faqs = [
    {
      question: 'What is your typical response time?',
      answer: 'We typically respond to inquiries within 2 business hours. For urgent matters, please call our support line.'
    },
    {
      question: 'Do you offer free consultations?',
      answer: 'Yes! We offer free 30-minute consultations to discuss your project requirements and provide initial guidance.'
    },
    {
      question: 'What information should I prepare before contacting you?',
      answer: 'It helps to have a brief description of your project, timeline, budget range, and any specific requirements you have.'
    },
    {
      question: 'Do you work with international clients?',
      answer: 'Absolutely! We work with clients from all over the world. We\'re experienced in managing different time zones and communication styles.'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, submitted: false, error: null });

    // Simulate API call
    setTimeout(() => {
      setFormStatus({ submitting: false, submitted: true, error: null });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        budget: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus({ submitting: false, submitted: false, error: null });
      }, 5000);
    }, 1500);
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Get In <span className="text-blue-200">Touch</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Ready to start your next project? Contact us for a free consultation 
              and let's bring your ideas to life.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center">
                <Headphones className="w-5 h-5 mr-2" />
                <span>24/7 Support Available</span>
              </div>
              <div className="h-4 w-px bg-white/30"></div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <span>2-hour Response Time</span>
              </div>
              <div className="h-4 w-px bg-white/30"></div>
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                <span>100% Confidential</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-blue-300 transition-all"
              >
                <div className={`w-12 h-12 rounded-lg ${colorClasses[method.color]} flex items-center justify-center mb-4`}>
                  {method.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{method.title}</h3>
                <div className="space-y-1 mb-3">
                  {method.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600">{detail}</p>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mb-4">{method.description}</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`text-sm font-semibold px-4 py-2 rounded-lg transition ${
                    method.color === 'blue' ? 'bg-blue-600 text-white hover:bg-blue-700' :
                    method.color === 'green' ? 'bg-green-600 text-white hover:bg-green-700' :
                    method.color === 'purple' ? 'bg-purple-600 text-white hover:bg-purple-700' :
                    'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  {method.action}
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-1 space-y-8"
            >
              {/* Office Locations */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <MapPin className="mr-2 text-blue-600" />
                  Our Offices
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Headquarters</h4>
                    <p className="text-gray-600">
                      123 Tech Street<br />
                      Silicon Valley<br />
                      San Francisco, CA 94107<br />
                      United States
                    </p>
                    <div className="flex items-center mt-2 text-blue-600">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      <span className="text-sm">Get Directions</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Development Center</h4>
                    <p className="text-gray-600">
                      456 Innovation Drive<br />
                      Tech Park<br />
                      Austin, TX 73301<br />
                      United States
                    </p>
                    <div className="flex items-center mt-2 text-blue-600">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      <span className="text-sm">Get Directions</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <Clock className="mr-2 text-blue-600" />
                  Business Hours
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Saturday</span>
                    <span className="font-semibold">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sunday</span>
                    <span className="font-semibold">Closed</span>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="flex items-center text-green-600">
                      <Headphones className="w-4 h-4 mr-2" />
                      <span className="text-sm">24/7 Emergency Support Available</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <Globe className="mr-2 text-blue-600" />
                  Connect With Us
                </h3>
                <p className="text-gray-600 mb-4">
                  Follow us for updates, tips, and insights
                </p>
                <div className="flex space-x-3">
                  {[
                    { icon: <Linkedin />, label: 'LinkedIn', color: 'bg-blue-600 hover:bg-blue-700' },
                    { icon: <Twitter />, label: 'Twitter', color: 'bg-sky-500 hover:bg-sky-600' },
                    { icon: <Facebook />, label: 'Facebook', color: 'bg-blue-800 hover:bg-blue-900' },
                    { icon: <Instagram />, label: 'Instagram', color: 'bg-pink-600 hover:bg-pink-700' }
                  ].map((social) => (
                    <motion.a
                      key={social.label}
                      whileHover={{ scale: 1.1, y: -5 }}
                      href="#"
                      className={`w-12 h-12 ${social.color} rounded-lg flex items-center justify-center text-white transition`}
                      aria-label={social.label}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Support Statistics */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold mb-4">Our Support Promise</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3" />
                    <span>2-hour average response time</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="w-5 h-5 mr-3" />
                    <span>4.9/5 customer satisfaction</span>
                  </div>
                  <div className="flex items-center">
                    <Zap className="w-5 h-5 mr-3" />
                    <span>99.9% uptime guarantee</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {/* Form Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
                  <h2 className="text-2xl font-bold mb-2">Send Us a Message</h2>
                  <p className="text-blue-100">
                    Fill out the form below and we'll get back to you within 2 business hours
                  </p>
                </div>

                {/* Form Status Messages */}
                {formStatus.submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mx-6 mt-6 bg-green-50 border border-green-200 rounded-lg p-4"
                  >
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                      <div>
                        <h4 className="font-semibold text-green-900">Message Sent Successfully!</h4>
                        <p className="text-green-700 text-sm mt-1">
                          Thank you for contacting us. We'll get back to you within 2 business hours.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {formStatus.error && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mx-6 mt-6 bg-red-50 border border-red-200 rounded-lg p-4"
                  >
                    <div className="flex items-center">
                      <XCircle className="w-5 h-5 text-red-600 mr-3" />
                      <div>
                        <h4 className="font-semibold text-red-900">Error Sending Message</h4>
                        <p className="text-red-700 text-sm mt-1">
                          Please try again or contact us directly via phone.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Contact Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        placeholder="+92 304 330 9005"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        placeholder="Your Company Inc."
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">
                        Service Interested In *
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">
                        Project Budget *
                      </label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                      >
                        <option value="">Select budget range</option>
                        {budgets.map((budget) => (
                          <option key={budget} value={budget}>{budget}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">
                      Project Details *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="Tell us about your project, timeline, specific requirements, and any other details..."
                    />
                  </div>

                  {/* File Upload (Optional) */}
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">
                      Attach Files (Optional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">Drop files here or click to upload</p>
                      <p className="text-sm text-gray-500">
                        Maximum file size: 10MB (PDF, DOC, JPG, PNG)
                      </p>
                      <input
                        type="file"
                        className="hidden"
                        id="file-upload"
                        multiple
                      />
                      <label
                        htmlFor="file-upload"
                        className="inline-block mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition cursor-pointer"
                      >
                        Browse Files
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={formStatus.submitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-4 rounded-lg font-semibold transition flex items-center justify-center ${
                      formStatus.submitting
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    } text-white`}
                  >
                    {formStatus.submitting ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </motion.button>

                  <p className="text-center text-gray-500 text-sm">
                    By submitting this form, you agree to our Privacy Policy and Terms of Service.
                  </p>
                </form>
              </div>

              {/* FAQ Section */}
              <div className="mt-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <HelpCircle className="mr-2 text-blue-600" />
                  Frequently Asked Questions
                </h3>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                    >
                      <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                      <p className="text-gray-600">{faq.answer}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Find Our <span className="text-blue-600">Offices</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Visit us at our offices or connect with us remotely from anywhere in the world.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Headquarters Map */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
              <div className="h-64 bg-gradient-to-r from-blue-500 to-blue-600 relative">
                {/* Mock Map */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-center">
                    <MapPin className="w-12 h-12 mx-auto mb-4" />
                    <div className="text-xl font-semibold">Silicon Valley HQ</div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Headquarters</h3>
                <p className="text-gray-600 mb-4">
                  123 Tech Street, Silicon Valley<br />
                  San Francisco, CA 94107<br />
                  United States
                </p>
                <div className="flex items-center text-blue-600">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  <span className="font-medium">Open in Google Maps</span>
                </div>
              </div>
            </div>

            {/* Development Center Map */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
              <div className="h-64 bg-gradient-to-r from-green-500 to-green-600 relative">
                {/* Mock Map */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-center">
                    <MapPin className="w-12 h-12 mx-auto mb-4" />
                    <div className="text-xl font-semibold">Austin Development Center</div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Development Center</h3>
                <p className="text-gray-600 mb-4">
                  456 Innovation Drive, Tech Park<br />
                  Austin, TX 73301<br />
                  United States
                </p>
                <div className="flex items-center text-blue-600">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  <span className="font-medium">Open in Google Maps</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Don't wait any longer. Contact us today and let's build something amazing together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center justify-center"
              >
                <Phone className="mr-2" size={20} />
                Call Now: +92 304 330 9005
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition flex items-center justify-center"
              >
                <Calendar className="mr-2" size={20} />
                Schedule Free Consultation
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;